// Lib
import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { DragSourceMonitor, useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import * as Immutable from 'immutable';

// Singleton
import dragAndDropStateSingleton from './dragAndDropStateSingleton';

// Utils
import platformSingleton from '../../platform/platformSingleton';
import * as elementFactory from '../../../common/elements/elementRegistry';
import { isBoardLike, isColorSwatch, isColumn } from '../../../common/elements/utils/elementTypeUtils';
import { getElementId, getIsCollapsed } from '../../../common/elements/utils/elementPropertyUtils';
import translateDropResultIntoElementLocation from './utils/translateDropResultIntoElementLocation';
import { focusFakeInput } from '../../mobile/boardsPage/editing/touchScreenFocusUtils';
import { usePerformHapticFeedback, HapticsStyle } from '../../utils/haptics/usePerformHapticFeedback';
import { usePlaySound } from '../../sounds/usePlaySound';

// Actions
import { createAndEditElement } from '../actions/elementActions';
import { endOperation } from '../../utils/undoRedo/undoRedoActions';
import { dragEnd, dragStart } from '../../reducers/draggingActions';
import { closePopupsMatching } from '../../components/popupPanel/popupActions';

// Selectors
import { contentLimitExceededSelector } from '../../user/elementCount/elementCountSelector';

// Constants
import { ELEMENT_DND_TYPE } from '../../../common/elements/elementConstants';
import { BoardSections } from '../../../common/boards/boardConstants';
import { DRAG_PREVIEW_ID } from '../../reducers/draggingConstants';
import { SoundId } from '../../sounds/soundConstants';
import { MetaToolIds } from '../../workspace/toolbar/config/toolDefinitions/toolbarMetaToolConfig';

// Types
import { ElementType } from '../../../common/elements/elementTypes';
import { DropResult } from '../../canvas/dnd/dragTypes';
import { ImMNElement, MNElementContent, MNElementLocation } from '../../../common/elements/elementModelTypes';

// Styles
import './DraggableElementCreationTool.scss';

// TODO: This represents the props that the consumer of this component will provide to
//  this component - I wasn't sure how to type this!
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConsumerProvidedProps = any;

type DraggableElementCreationToolProps = {
    gridSize: number;
    elementType: ElementType;
    currentBoardId: string;
    currentUserId: string;
    className?: string;
    /** Play the toolbar rollover sound on hover (only wanted for the main toolbar tools) */
    playRolloverSound?: boolean;
    children: React.ReactNode;
    creationSource?: string;
    focusInputEditorType?: string;

    // Not sure how to type the consumer props
    getDefaultContent?: (props: ConsumerProvidedProps) => MNElementContent;
    beforeDragStart?: (props: ConsumerProvidedProps) => void;
    beforeDragEnd?: (dragSuccess: boolean) => void;

    getElementScaledCustomDragOffset: (
        props: ConsumerProvidedProps,
        monitor: DragSourceMonitor,
        element: ImMNElement,
    ) => { x: number; y: number };
    getElementScaledGrabOffset: (
        props: ConsumerProvidedProps,
        monitor: DragSourceMonitor,
        element: ImMNElement,
    ) => { x: number; y: number };

    getContextZoomScale?: () => number;

    selectOnCreate?: boolean;
    editOnCreate?: boolean;

    // Drag and drop options
    customOnDrop?: (props: OnDropProps) => Promise<string | void>;
    delayTouchStart?: number;
    dragAngleRanges?: { start: number; end: number }[];

    onTouchStart?: (event: React.TouchEvent) => void;
    onTouchEnd?: (event: React.TouchEvent) => void;
    onMouseDown?: (event: React.MouseEvent) => void;
    onMouseUp?: (event: React.MouseEvent) => void;

    onDragEnd?: (dragSuccess: boolean, createdElementId?: string) => void;
};

type OnDropProps = {
    elementType: ElementType;
    location: MNElementLocation;
    content: MNElementContent;
    currentBoardId: string;
    transactionId: number;
    select?: boolean;
    edit?: boolean;
    creationSource?: string;
};

const defaultGetDefaultContentFn = () => ({});

/* WORKAROUND:
 * This is a synthetic event that we're creating to ensure that the React "SelectEventPlugin" works with
 * the drag and drop (react-dnd) and text editing (draft-js) libraries.
 *
 * React adds an "onSelect" event to content editable elements.  This is not standard in HTML, but the
 * Draft JS text editor relies on this event to track the position of the cursor or the selection state.
 *
 * The SelectEventPlugin prevents the onSelect event whenever a drag occurs (in case the user is selecting,
 * so it has a flag variable "mouseDown" that gets set to true on the mouseDown event and false on the
 * mouseUp event.
 * We're using the HTML5 backend of the React DnD library, so it uses the standard HTML5 drag and drop
 * events.  With HTML5 drag and drop the mouseMove and mouseUp events are not fired once a drag starts.
 * Thus the React "SelectEventPlugin" receives a "mouseDown" event, but never receives a mouse up event
 * when the drop finishes.
 * As such the SelectEventPlugin doesn't set mouseDown flag to false, and the onSelect event will not fire
 * until a future mouse click forces the mouseDown flag back to false.
 *
 * Because we immediately set the element into its editing state when an element tool drop occurs, the
 * onSelect event is not being fired.
 *
 * By firing this synthetic event we force the SelectEventPlugin to set its mouseDown flag to false, and as
 * such the onSelect events will now fire when entering the editing mode after being dropped onto the
 * canvas.
 */
const triggerSyntheticMouseUpEvent = () => {
    const event = new MouseEvent('mouseup', { bubbles: true });
    document.body.dispatchEvent(event);
};

/**
 * This function improves the behaviour of editing on certain devices, such as iPads,
 * by immediately focusing a "fake input", and then allowing focus to move to the new element's
 * input/content editable.
 * It seems that these devices don't allow asynchronous focus shifting to places far from where
 * the cursor currently is, for some reason. So this will shift the cursor to the place where
 * the element is dropped, and then when the new element is created it will take the cursor back
 * itself.
 */
const handleFakeDropFocus = (dropResult: DropResult, domNode?: HTMLElement | null, inputType?: string) => {
    if (!platformSingleton.features.isTouch) return;
    if (!domNode) return;
    if (!dropResult?.clientOffset) return;

    const { clientOffset } = dropResult;

    focusFakeInput(clientOffset, domNode, {
        inputType,
    });
};

export const wasDraggableElementDragSuccess = (
    props: DraggableElementCreationToolProps,
    monitor: DragSourceMonitor,
) => {
    // Doing nothing if it was not dropped onto a compatible target
    if (!monitor.didDrop()) return false;

    const dropResult = monitor.getDropResult();

    // do nothing if no valid location was returned
    if (!dropResult.location) return false;

    // don't allow dropping onto trash
    if (dropResult.location.section === BoardSections.TRASH) return false;

    return true;
};

/**
 * Determines whether the newly created element should be selected and/or edited based on where
 * the new element is being created.
 */
const shouldSelectOrEditOnCreate = (dropResult: DropResult, currentBoardId: string) => {
    const { dropTargetElement } = dropResult;

    if (!dropTargetElement) return false;

    // When dropping onto the current board we always want to select and edit the new element
    if (getElementId(dropTargetElement) === currentBoardId) return true;

    // If dropping onto a sub-board or alias we never want to select or edit the new element
    if (isBoardLike(dropTargetElement)) return false;

    // Don't select when dropping onto collapsed columns
    if (isColumn(dropTargetElement) && getIsCollapsed(dropTargetElement)) return false;

    // When dropping anywhere else, do select and edit
    return true;
};

const DraggableElementCreationTool = (props: DraggableElementCreationToolProps) => {
    const {
        beforeDragStart,
        beforeDragEnd,
        customOnDrop,
        elementType,
        getDefaultContent = defaultGetDefaultContentFn,
        currentBoardId,
        selectOnCreate = true,
        editOnCreate = true,
        creationSource,
        focusInputEditorType,
        onDragEnd,
        gridSize,
        children,
        className,
        playRolloverSound,
        delayTouchStart,
        dragAngleRanges,

        getElementScaledCustomDragOffset,
        getElementScaledGrabOffset,
        currentUserId,
        getContextZoomScale = () => 1,

        onTouchStart,
        onTouchEnd,
        onMouseDown,
        onMouseUp,
    } = props;

    const dispatch = useDispatch();
    const performHapticFeedback = usePerformHapticFeedback();
    const playSound = usePlaySound();

    const isContentLimitExceeded = useSelector(contentLimitExceededSelector);

    const startDragging = ({ source }: { source?: string }) => dispatch(dragStart({ source, elementType }));
    const endDragging = (dropState: Record<string, any>, dropLocation?: MNElementLocation) =>
        dispatch(dragEnd(dropState, dropLocation));
    const dispatchEndMoveOperation = () => dispatch(endOperation('move'));
    const onDrop = ({
        elementType,
        location,
        content,
        currentBoardId,
        transactionId,
        select,
        edit = true,
        creationSource,
    }: OnDropProps) => {
        const result = dispatch(
            createAndEditElement({
                elementType,
                location,
                content,
                currentBoardId,
                transactionId,
                select,
                edit,
                creationSource,
                focusInputEditorType,
            }),
        );

        select &&
            dispatch(
                closePopupsMatching({
                    predicateFn: (activePopupId: string) => activePopupId.startsWith(MetaToolIds.MORE),
                }),
            );

        return result;
    };

    const fakeDropRef = useRef<HTMLDivElement>(null);

    const [{ isDragging }, connectDragSource, connectDragPreview] = useDrag<any, DropResult, { isDragging: boolean }>({
        item: { id: DRAG_PREVIEW_ID, type: ELEMENT_DND_TYPE },
        begin: (monitor) => {
            beforeDragStart?.(props);

            performHapticFeedback(HapticsStyle.ImpactMedium);

            startDragging({ source: creationSource });

            const element = Immutable.fromJS(
                elementFactory.createElementObject({
                    id: DRAG_PREVIEW_ID,
                    elementType,
                    content: getDefaultContent(props),
                    location: {},
                    meta: {
                        creator: currentUserId,
                    },
                }),
            );

            const elementId = getElementId(element);
            const zoomScaleOnDragSource = getContextZoomScale();

            // Need to do this to ensure that if a comment switches to its collapsed form, it's situated
            // on the mouse correctly
            dragAndDropStateSingleton.scaledGrabOffset = getElementScaledGrabOffset(props, monitor, element);

            // Create
            const scaledCustomDragOffset = getElementScaledCustomDragOffset(props, monitor, element);

            return {
                id: DRAG_PREVIEW_ID,
                element,
                isNewElement: true,
                draggedElementIds: [elementId],
                draggedElements: [element],
                zoomScaleOnDragSource,
                scaledCustomDragOffset,
                hoveredTypesRegistry: {},
            };
        },
        end: async (item, monitor) => {
            const dragSuccess = wasDraggableElementDragSuccess(props, monitor);

            beforeDragEnd?.(dragSuccess);

            const operationTransactionId = dispatchEndMoveOperation();

            if (!dragSuccess) {
                endDragging({ source: creationSource });
                onDragEnd?.(false);
                endDragging({ source: creationSource });
                return;
            }

            const dropResult = monitor.getDropResult();
            const { unscaledElementOffsetsMap, element, scaledCustomDragOffset } = monitor.getItem();

            const location = translateDropResultIntoElementLocation({
                dropResult,
                unscaledElementOffsetsMap,
                gridSize,
                elementId: getElementId(element),
                scaledCustomDragOffset,
            });

            const shouldSelectOrEdit = shouldSelectOrEditOnCreate(dropResult, currentBoardId);
            const shouldEdit = editOnCreate && shouldSelectOrEdit;
            const shouldSelect = selectOnCreate && shouldSelectOrEdit;

            // If the content limit is exceeded, we don't want to show the editor as the element won't be created
            if (shouldEdit && !isContentLimitExceeded)
                handleFakeDropFocus(dropResult, fakeDropRef.current, focusInputEditorType);

            triggerSyntheticMouseUpEvent();

            const transactionId = dropResult.transactionId || operationTransactionId;

            const onDropFn = customOnDrop || onDrop;

            const createdElementId = await onDropFn({
                elementType,
                location,
                content: getDefaultContent(props),
                currentBoardId,
                transactionId,
                creationSource,
                select: shouldSelect,
                edit: shouldEdit,
            });

            if (createdElementId) {
                const dropState = dropResult ? dropResult.dropState : null;
                endDragging(
                    {
                        ...dragAndDropStateSingleton,
                        ...dropState,
                        source: creationSource,
                        droppedElementIds: [createdElementId],
                    },
                    location,
                );
            } else {
                endDragging({ source: creationSource });
            }

            onDragEnd?.(true, createdElementId as string);
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        options: {
            // @ts-ignore - these are custom options that we're passing to the drag & drop backend
            delayTouchStart,
            dragAngleRanges,
        },
    });

    useEffect(() => {
        if (!connectDragPreview) return;
        connectDragPreview(getEmptyImage());
    }, [connectDragPreview]);

    return connectDragSource(
        <div
            ref={fakeDropRef}
            className={classNames('DraggableElementCreationTool', className, { dragging: isDragging })}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseEnter={() => {
                if (!playRolloverSound || isDragging) return;
                playSound(isColorSwatch(elementType) ? SoundId.DoubleRollOver : SoundId.RollOver);
            }}
        >
            {children}
        </div>,
    );
};

export default DraggableElementCreationTool;
